/**
 * SitesGrid — the hub's Sites card (White signal v2, Paper "App v3 · Home · LP-aligned").
 *
 * One raised card: header (title, count, search) → column row → workspace
 * group rows (44px, always-visible `Open ›` + `Workspace settings`) → site
 * rows (52px). Reporting is month-to-date: "Visitors MTD" is unique visitors
 * this month so far and "vs {last month}" compares it to the same span last
 * month (SUPENG-96).
 *
 * Signals that earn space in a row: a live test (`bg-live` dot beside the
 * domain), a site whose tag was never verified and has sent nothing (warning
 * "No data" pill), and a verified site still waiting for its first visit (grey
 * "Waiting for first visit" pill, dashed trend, zeros). All three are mutually
 * exclusive; `siteTrackingState` decides the last two. Deltas are quiet ink: up is
 * foreground, down is meta gray, no arrows, no green. Exactly one row, the
 * worst drop at or past FLAG_DROP_PCT, gets the orange key-value chip and an
 * orange trend line: orange marks the answer, as on the marketing site.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { subMonths } from 'date-fns'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { HugeiconsIcon } from '@hugeicons/react'
import type { IconSvgElement } from '@hugeicons/react'
import {
  Search01Icon,
  MoreVerticalIcon,
  Settings01Icon,
  Settings02Icon,
  Delete01Icon,
  Analytics01Icon,
  TestTube01Icon,
  FilterIcon,
  Target02Icon,
  ArrowUp01Icon,
  ArrowDown01Icon,
  SourceCodeIcon,
  ArrowRight01Icon,
} from '@hugeicons/core-free-icons'
import Favicon from '@/components/shared/Favicon'
import { generateSlugId } from '@/lib/routing/slug-id'
import { cn } from '@/lib/utils'
import type { WeeklySummary } from '@/hooks/useAccountWoW'
import { SITE_TREND_DAYS, useSiteDailyVisitors } from '@/hooks/useSiteDailyVisitors'
import { Skeleton } from '@/components/ui/skeleton'
import { siteTrackingState } from './siteTrackingState'

interface WorkspaceAttachment {
  workspaceId: string
  name: string
  isAutoGrouped?: boolean
}

interface SitesGridProperty {
  propertyId: string
  name: string
  domain: string
  archived?: boolean
  isStaging?: boolean
  isDemo?: boolean
  stats?: { views?: number; events?: number }
  attachedWorkspaces?: WorkspaceAttachment[]
  trackingStatus?: 'not_installed' | 'pending_verification' | 'verified' | 'error'
  hasRecentViews?: boolean
  hasPreviousViews?: boolean
}

/** A month-over-month drop at or past this (in %) is the one that needs action. */
export const FLAG_DROP_PCT = -20

/** Unique visitors month to date for a property (`thisWeek` == MTD in useAccountWoW). */
function visitorsMTD(
  propertyId: string,
  byPropertyId?: Record<string, WeeklySummary>,
): number | undefined {
  return byPropertyId?.[propertyId]?.thisWeek?.sessions
}

/** Visitor MoM delta, rounded. Undefined when there is no prior span to compare. */
export function visitorDelta(summary?: WeeklySummary): number | undefined {
  if (!summary || (summary.lastWeek?.sessions ?? 0) <= 0) return undefined
  return Math.round(summary.change?.sessions ?? 0)
}

/**
 * The single site whose visitors dropped the most, if that drop is at or past
 * FLAG_DROP_PCT. Shared by the hub headline and the table so both name the
 * same site.
 */
export function pickFlaggedDrop(
  properties: Array<Pick<SitesGridProperty, 'propertyId' | 'domain' | 'name' | 'isDemo' | 'archived'>>,
  byPropertyId?: Record<string, WeeklySummary>,
): { propertyId: string; domain: string; delta: number } | null {
  let worst: { propertyId: string; domain: string; delta: number } | null = null
  for (const p of properties) {
    if (p.isDemo || p.archived) continue
    const delta = visitorDelta(byPropertyId?.[p.propertyId])
    if (delta === undefined || delta > FLAG_DROP_PCT) continue
    if (!worst || delta < worst.delta) {
      worst = { propertyId: p.propertyId, domain: p.domain || p.name, delta }
    }
  }
  return worst
}

interface Props {
  properties: SitesGridProperty[]
  /** Month-to-date summaries from `useAccountWoW`: source for Visitors MTD and the MoM delta. */
  byPropertyId?: Record<string, WeeklySummary>
  /** The one flagged drop (see `pickFlaggedDrop`). Only this row turns orange. */
  flaggedPropertyId?: string | null
  testCountsByProperty?: Record<string, { active: number; readyToCall: number; total: number }>
  resolveSitePath: (propertyId: string, subPath: string) => string
  onOpenSiteSettings: (
    property: SitesGridProperty,
    opts?: { tab?: 'install' | 'general' },
  ) => void
  onArchiveRequest?: (property: SitesGridProperty) => void
  onSiteArchived?: () => void
  /** Async data still loading: skeletons in the Visitors / delta cells so
   *  "data pending" reads differently from "no data". */
  isLoading?: boolean
}

type SortKey = 'views' | 'name' | 'mom' | 'events' | 'tests'
type SortDir = 'asc' | 'desc'

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

interface EnrichedRow {
  property: SitesGridProperty
  views: number
  events: number
  delta: number | undefined
  testCount: number
  /** At least one active test: surfaced as a live dot beside the domain. */
  live: boolean
  /** Never verified and no traffic in MTD or the last 14 days. Actionable, so it earns a pill. */
  needsInstall: boolean
  /** Verified, but nothing has arrived yet. Grey pill, dashed trend, zeros. */
  waiting: boolean
}

// Paper meta gray (#00000066) on white. Request: a `--ui-meta` token.
const META = 'text-foreground/45'
const ROW_BORDER = 'border-b border-border/60'
/** Secondary button chrome from the artboard (white, hairline ring, 1px drop). */
const QUIET_BUTTON =
  'inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-card px-2.5 text-[13px] font-medium leading-4 text-foreground shadow-[0_0_0_1px_var(--border),0_1px_2px_rgb(0_0_0/0.04)] transition-colors hover:bg-muted [&_svg]:block [&_svg]:shrink-0'

// ── Component ───────────────────────────────────────────────────────────────

export default function SitesGrid({
  properties,
  byPropertyId,
  flaggedPropertyId,
  testCountsByProperty,
  resolveSitePath,
  onOpenSiteSettings,
  onArchiveRequest,
  isLoading,
}: Props) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('views')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key)
      setSortDir('desc')
    } else if (sortDir === 'desc') {
      setSortDir('asc')
    } else {
      setSortKey('views')
      setSortDir('desc')
    }
  }

  // "vs August": the comparison span is the same days last month.
  const priorMonthLabel = useMemo(
    () => subMonths(new Date(), 1).toLocaleDateString('en-US', { month: 'long' }),
    [],
  )

  // Filter + decorate
  const enriched = useMemo<EnrichedRow[]>(() => {
    return properties
      .filter((p) => !p.archived && !p.isDemo)
      .filter((p) => {
        if (!search.trim()) return true
        const q = search.toLowerCase()
        return (
          (p.name || '').toLowerCase().includes(q) ||
          (p.domain || '').toLowerCase().includes(q) ||
          (p.attachedWorkspaces?.[0]?.name || '').toLowerCase().includes(q)
        )
      })
      .map((p) => {
        const views = visitorsMTD(p.propertyId, byPropertyId) ?? 0
        const events = p.stats?.events ?? 0
        const delta = visitorDelta(byPropertyId?.[p.propertyId])
        const testCount = testCountsByProperty?.[p.propertyId]?.active ?? 0
        // Same rule as WorkspaceHub's banner and notice (`siteTrackingState`).
        const tracking = siteTrackingState(p)
        return {
          property: p,
          views,
          events,
          delta,
          testCount,
          live: testCount > 0,
          needsInstall: tracking === 'install',
          waiting: tracking === 'waiting',
        }
      })
  }, [properties, search, byPropertyId, testCountsByProperty])

  // Group by workspace (first attached workspace; "Unsorted" otherwise)
  const groups = useMemo(() => {
    const map = new Map<string, { workspaceId: string | null; name: string; rows: EnrichedRow[] }>()
    for (const row of enriched) {
      const ws = row.property.attachedWorkspaces?.[0]
      const key = ws?.workspaceId ?? '__unsorted__'
      const name = ws?.name ?? 'Unsorted'
      if (!map.has(key)) {
        map.set(key, { workspaceId: ws?.workspaceId ?? null, name, rows: [] })
      }
      map.get(key)!.rows.push(row)
    }
    const groupsArr = Array.from(map.values())
    const sorter = (a: EnrichedRow, b: EnrichedRow): number => {
      const dir = sortDir === 'asc' ? 1 : -1
      switch (sortKey) {
        case 'name':
          return dir * (a.property.domain || '').localeCompare(b.property.domain || '')
        case 'mom':
          return dir * ((a.delta ?? 0) - (b.delta ?? 0))
        case 'events':
          return dir * (a.events - b.events)
        case 'tests':
          return dir * (a.testCount - b.testCount)
        case 'views':
        default:
          return dir * (a.views - b.views)
      }
    }
    for (const g of groupsArr) g.rows.sort(sorter)
    // Largest portfolio segment first
    groupsArr.sort((a, b) => {
      const sumA = a.rows.reduce((s, r) => s + r.views, 0)
      const sumB = b.rows.reduce((s, r) => s + r.views, 0)
      return sumB - sumA
    })
    return groupsArr
  }, [enriched, sortKey, sortDir])

  const totalCount = enriched.length
  const isSearching = search.trim().length > 0
  const trackedCount = properties.filter((p) => !p.archived && !p.isDemo).length

  const sortProps = { currentKey: sortKey, dir: sortDir, onToggle: toggleSort }

  return (
    <TooltipProvider delayDuration={200}>
      <section className="surface-raised overflow-clip">
        {/* Card header — 56px */}
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-border/80 px-4">
          <h2 className="text-sm font-semibold leading-[18px] text-foreground">Sites</h2>
          <span className={cn('text-[13px] leading-4 tabular-nums', META)}>
            {trackedCount} tracked
          </span>
          <div className="flex-1" />
          <label className="hidden h-8 w-[220px] shrink-0 items-center gap-2 rounded-lg bg-card px-2.5 shadow-[0_0_0_1px_var(--border)] focus-within:shadow-[0_0_0_1px_var(--ring)] sm:flex">
            <HugeiconsIcon icon={Search01Icon} size={13} className="shrink-0 text-foreground/40" />
            <input
              type="text"
              placeholder="Find a site"
              aria-label="Find a site"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent text-[13px] leading-4 text-foreground outline-none placeholder:text-foreground/45"
            />
          </label>
        </div>

        {totalCount === 0 && isSearching ? (
          <div className={cn('px-4 py-12 text-center text-[13px]', META)}>
            No sites match "{search}"
          </div>
        ) : (
          <table className="w-full table-fixed border-collapse text-left">
            {/* Artboard lanes: Site 290 · Trend 110 · Visitors 80 · vs 90 · Events 80 · Tests 92 · Actions (rest).
                The first and last lanes carry the row's 16px inline padding. */}
            <colgroup>
              <col className="lg:w-[306px]" />
              <col className="hidden w-[110px] md:table-column" />
              <col className="w-[80px]" />
              <col className="hidden w-[90px] md:table-column" />
              <col className="hidden w-[80px] lg:table-column" />
              <col className="hidden w-[92px] lg:table-column" />
              <col className="hidden lg:table-column" />
              <col className="w-[46px]" />
            </colgroup>
            <thead>
              <tr className={cn('h-[34px]', ROW_BORDER)}>
                <ColHeader label="Site" sortKey="name" className="pl-4" {...sortProps} />
                <th scope="col" className="hidden p-0 align-middle md:table-cell">
                  <span className={cn('text-xs font-medium leading-[14px]', META)} title="Unique visitors a day, last 14 days">Last 14 days</span>
                </th>
                <ColHeader label="Visitors MTD" sortKey="views" align="right" title="Unique visitors this month so far" {...sortProps} />
                <ColHeader
                  label={`vs ${priorMonthLabel}`}
                  sortKey="mom"
                  align="right"
                  className="hidden md:table-cell"
                  title={`Unique visitors this month so far vs the same days in ${priorMonthLabel}`}
                  {...sortProps}
                />
                <ColHeader label="Events" sortKey="events" align="right" className="hidden lg:table-cell" {...sortProps} />
                <ColHeader label="Tests" sortKey="tests" align="right" className="hidden lg:table-cell" {...sortProps} />
                <th scope="col" colSpan={2} className="hidden p-0 pr-4 text-right align-middle lg:table-cell">
                  <span className={cn('text-xs font-medium leading-[14px]', META)}>Actions</span>
                </th>
                <th scope="col" className="p-0 pr-4 lg:hidden" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {groups.map((group, gi) => (
                <GroupRows
                  key={group.workspaceId ?? `unsorted-${gi}`}
                  group={group}
                  flaggedPropertyId={flaggedPropertyId}
                  resolveSitePath={resolveSitePath}
                  onOpenSiteSettings={onOpenSiteSettings}
                  onArchive={onArchiveRequest}
                  isLoading={isLoading}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </TooltipProvider>
  )
}

// ── Column header ───────────────────────────────────────────────────────────

function ColHeader({
  label,
  sortKey,
  currentKey,
  dir,
  onToggle,
  align = 'left',
  className,
  title,
}: {
  label: string
  sortKey: SortKey
  currentKey: SortKey
  dir: SortDir
  onToggle: (k: SortKey) => void
  align?: 'left' | 'right'
  className?: string
  /** Native title: hover explanation (e.g. the MoM math). */
  title?: string
}) {
  const active = sortKey === currentKey
  // The default order (Visitors, descending) is not announced with an arrow;
  // any sort the user picked is.
  const showArrow = active && !(sortKey === 'views' && dir === 'desc')
  return (
    <th
      scope="col"
      className={cn('p-0 align-middle', align === 'right' ? 'text-right' : 'text-left', className)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        title={title}
        className={cn(
          'inline-flex items-center gap-1 text-xs font-medium leading-[14px] transition-colors hover:text-foreground',
          showArrow ? 'text-foreground' : 'text-foreground/45',
        )}
      >
        {label}
        {showArrow && (
          <HugeiconsIcon icon={dir === 'asc' ? ArrowUp01Icon : ArrowDown01Icon} size={10} />
        )}
      </button>
    </th>
  )
}

// ── Group rows ──────────────────────────────────────────────────────────────

function GroupRows({
  group,
  flaggedPropertyId,
  resolveSitePath,
  onOpenSiteSettings,
  onArchive,
  isLoading,
}: {
  group: { workspaceId: string | null; name: string; rows: EnrichedRow[] }
  flaggedPropertyId?: string | null
  resolveSitePath: (propertyId: string, subPath: string) => string
  onOpenSiteSettings: (
    p: SitesGridProperty,
    opts?: { tab?: 'install' | 'general' },
  ) => void
  onArchive?: (p: SitesGridProperty) => void
  isLoading?: boolean
}) {
  const navigate = useNavigate()
  const wsSlugId = group.workspaceId ? generateSlugId(group.name, group.workspaceId) : null
  return (
    <>
      {/* Workspace row — 44px on the #FAFAFA surface. Actions always visible, labeled. */}
      <tr className={cn('h-11 bg-muted/60', ROW_BORDER)}>
        <td colSpan={8} className="px-4 py-0 align-middle">
          <div className="flex items-center gap-2">
            <span className="truncate text-[13px] font-semibold leading-4 text-foreground">
              {group.name}
            </span>
            <span className={cn('shrink-0 text-xs leading-4 tabular-nums', META)}>
              {group.rows.length} site{group.rows.length === 1 ? '' : 's'}
            </span>
            <div className="flex-1" />
            {wsSlugId && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`/workspace/${wsSlugId}`)
                  }}
                  className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[13px] font-medium leading-4 text-foreground transition-colors hover:bg-foreground/[0.04] [&_svg]:block"
                >
                  Open
                  <HugeiconsIcon icon={ArrowRight01Icon} size={12} className="shrink-0" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`/workspace/${wsSlugId}/settings`)
                  }}
                  className={QUIET_BUTTON}
                >
                  <HugeiconsIcon icon={Settings01Icon} size={13} className="text-muted-foreground" />
                  <span className="lg:hidden">Workspace</span>
                  <span className="hidden lg:inline">Workspace settings</span>
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {group.rows.map((row) => (
        <SiteRow
          key={row.property.propertyId}
          {...row}
          flagged={row.property.propertyId === flaggedPropertyId}
          resolveSitePath={resolveSitePath}
          onOpenSiteSettings={onOpenSiteSettings}
          onArchive={onArchive}
          isLoading={isLoading}
        />
      ))}
    </>
  )
}

// ── Trend bars ──────────────────────────────────────────────────────────────

/**
 * 90×20 daily bars: unique visitors per day, last 14 days (Paper chart
 * system, in-table bars: accent tint, today's still-counting bar in brand
 * orange). Real buckets only: nothing renders until they load. Replaced a
 * seeded line that only tilted toward the MoM delta.
 */
function TrendBars({ propertyId, domain }: { propertyId: string; domain: string }) {
  const { data } = useSiteDailyVisitors(propertyId)
  if (!data || data.length === 0) {
    return <span aria-hidden className="block h-5 w-[90px] animate-pulse rounded-sm bg-muted/60" />
  }
  const days = data.slice(-SITE_TREND_DAYS)
  const max = Math.max(1, ...days.map((d) => d.visitors))
  const gap = 2
  const w = (90 - gap * (days.length - 1)) / days.length
  // Daily uniques are not additive (one visitor on two days counts twice), so
  // the label names the peak day, never a summed total.
  const peak = days.reduce((best, d) => (d.visitors > best.visitors ? d : best), days[0])
  return (
    <svg
      width={90}
      height={20}
      viewBox="0 0 90 20"
      className="block"
      role="img"
      aria-label={`${domain}: daily visitors over the last ${days.length} days, peak ${peak.visitors.toLocaleString('en-US')} on ${peak.day.slice(0, 10)}`}
    >
      {days.map((d, i) => {
        const h = d.visitors > 0 ? Math.max(2, (d.visitors / max) * 20) : 1
        const today = i === days.length - 1
        return (
          <rect
            key={d.day}
            x={i * (w + gap)}
            y={20 - h}
            width={w}
            height={h}
            rx={1}
            fill={today ? 'var(--chart-1)' : d.visitors > 0 ? 'var(--primary-tint-ring)' : 'var(--border)'}
          >
            <title>{`${d.day.slice(0, 10)}: ${d.visitors.toLocaleString('en-US')} visitors${today ? ' (today, still counting)' : ''}`}</title>
          </rect>
        )
      })}
    </svg>
  )
}

// ── Site row ────────────────────────────────────────────────────────────────

function SiteRow({
  property,
  views,
  events,
  delta,
  testCount,
  live,
  needsInstall,
  waiting,
  flagged,
  resolveSitePath,
  onOpenSiteSettings,
  onArchive,
  isLoading,
}: EnrichedRow & {
  flagged: boolean
  resolveSitePath: (propertyId: string, subPath: string) => string
  onOpenSiteSettings: (
    p: SitesGridProperty,
    opts?: { tab?: 'install' | 'general' },
  ) => void
  onArchive?: (p: SitesGridProperty) => void
  isLoading?: boolean
}) {
  const navigate = useNavigate()
  const hasData = views > 0
  const hasDelta = typeof delta === 'number' && Math.abs(delta) >= 1
  const up = (delta ?? 0) > 0
  const domain = property.domain || property.name
  const initial = (domain || '?').replace(/^www\./, '').charAt(0).toUpperCase()

  const go = (subPath: string) => (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(resolveSitePath(property.propertyId, subPath))
  }

  return (
    <tr
      onClick={() => navigate(resolveSitePath(property.propertyId, '/traffic'))}
      className={cn('group h-[52px] cursor-pointer transition-colors hover:bg-muted/40', ROW_BORDER)}
    >
      {/* Site — favicon tile (22px) + domain */}
      <td className="py-0 pl-4 pr-3 align-middle">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex size-[22px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-muted shadow-[0_0_0_1px_var(--border)]">
            <Favicon
              domain={property.domain}
              size={14}
              className="block size-3.5"
              renderFallback={() => (
                <span className="text-xs font-semibold leading-[14px] text-foreground">{initial}</span>
              )}
            />
          </div>
          <span className="truncate text-sm font-medium leading-[18px] text-foreground">{domain}</span>
          {live && (
            <span
              className="size-1.5 shrink-0 rounded-full bg-live"
              aria-label="Test live"
              title="Test live"
            />
          )}
          {/* Mutually exclusive with the live dot: a site running a test has traffic. */}
          {!live && needsInstall && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onOpenSiteSettings?.(property, { tab: 'install' })
              }}
              className="shrink-0 rounded-md bg-warning-tint px-1.5 py-0.5 text-xs font-medium leading-[14px] text-warning shadow-[0_0_0_1px_color-mix(in_oklab,var(--warning)_20%,transparent)] transition-colors hover:bg-warning/10"
              title="No data received yet. Check the tracking script is live."
            >
              No data
            </button>
          )}
          {!live && waiting && (
            <span className="inline-flex h-5 shrink-0 items-center gap-[5px] rounded-md bg-muted px-[7px] text-xs font-medium leading-[14px] text-ui-ink">
              <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-foreground/35" />
              Waiting for first visit
            </span>
          )}
        </div>
      </td>

      {/* Trend */}
      <td className="hidden p-0 align-middle md:table-cell">
        {waiting ? (
          <svg width={90} height={20} viewBox="0 0 90 20" className="block text-foreground/20" aria-hidden>
            <path d="M0 14 L90 14" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeDasharray="3 3" />
          </svg>
        ) : hasData ? (
          <TrendBars propertyId={property.propertyId} domain={domain} />
        ) : (
          <span className={cn('text-[13px]', META)}>—</span>
        )}
      </td>

      {/* Visitors MTD */}
      <td className="p-0 text-right align-middle">
        {isLoading ? (
          <span className="ml-auto inline-block h-3 w-10 animate-pulse rounded-full bg-muted" aria-label="Loading" />
        ) : (
          <span className="text-sm font-medium leading-[18px] text-foreground tabular-nums">
            {hasData ? formatCompact(views) : waiting ? '0' : <span className={META}>—</span>}
          </span>
        )}
      </td>

      {/* vs last month — ink up, meta down; the one flagged drop is the orange chip */}
      <td className="hidden p-0 text-right align-middle md:table-cell">
        {isLoading ? (
          <span className="inline-block h-3 w-10 animate-pulse rounded-full bg-muted" aria-label="Loading" />
        ) : hasDelta && flagged ? (
          <span className="inline-flex rounded-md bg-primary-tint px-[7px] py-0.5 text-[13px] font-medium leading-4 text-primary-strong tabular-nums shadow-[0_0_0_1px_var(--primary-tint-ring)]">
            {'−'}
            {Math.abs(delta!)}%
          </span>
        ) : hasDelta ? (
          <span
            className={cn(
              'text-[13px] font-medium leading-4 tabular-nums',
              up ? 'text-foreground' : META,
            )}
          >
            {up ? '+' : '−'}
            {Math.abs(delta!)}%
          </span>
        ) : (
          <span className={cn('text-[13px] font-medium leading-4', META)}>{waiting ? '–' : '—'}</span>
        )}
      </td>

      {/* Events */}
      <td className="hidden p-0 text-right align-middle lg:table-cell">
        <span className="text-sm leading-[18px] text-foreground tabular-nums">
          {events > 0 ? formatCompact(events) : <span className={META}>{waiting ? '0' : '—'}</span>}
        </span>
      </td>

      {/* Tests */}
      <td className="hidden p-0 text-right align-middle lg:table-cell">
        <span className={cn('text-[13px] leading-4 tabular-nums', testCount > 0 ? 'text-foreground' : META)}>
          {testCount > 0 ? `${testCount} running` : 'None'}
        </span>
      </td>

      {/* Quick actions + Site settings — always visible */}
      <td className="hidden p-0 align-middle lg:table-cell">
        <div className="flex items-center justify-end gap-0.5">
          <RowAction icon={Analytics01Icon} label="Analytics" onClick={go('/traffic')} />
          <RowAction
            icon={TestTube01Icon}
            label={testCount > 0 ? `${testCount} active test${testCount === 1 ? '' : 's'}` : 'Experiments'}
            onClick={go('/split-testing')}
          />
          <RowAction icon={Target02Icon} label="Heatmaps" onClick={go('/heatmaps')} />
          <RowAction icon={FilterIcon} label="Funnels" onClick={go('/funnel-analyzer')} />
          <span className="mx-2 h-[18px] w-px shrink-0 bg-border" aria-hidden />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenSiteSettings(property)
                }}
                className={QUIET_BUTTON}
              >
                <HugeiconsIcon icon={Settings02Icon} size={13} className="text-muted-foreground" />
                Site settings
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              Install script, filters and reports for this site
            </TooltipContent>
          </Tooltip>
        </div>
      </td>

      {/* Overflow menu */}
      <td className="py-0 pl-0.5 pr-4 text-right align-middle">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex size-7 items-center justify-center rounded-md text-foreground/60 transition-colors hover:bg-muted hover:text-foreground [&_svg]:block"
              aria-label="More actions"
            >
              <HugeiconsIcon icon={MoreVerticalIcon} size={15} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={go('/traffic')} className="cursor-pointer text-xs">
              <HugeiconsIcon icon={Analytics01Icon} size={14} className="mr-2 text-muted-foreground" />
              Open analytics
            </DropdownMenuItem>
            <DropdownMenuItem onClick={go('/split-testing/new')} className="cursor-pointer text-xs">
              <HugeiconsIcon icon={TestTube01Icon} size={14} className="mr-2 text-muted-foreground" />
              Create split test
            </DropdownMenuItem>
            <DropdownMenuItem onClick={go('/heatmaps')} className="cursor-pointer text-xs">
              <HugeiconsIcon icon={Target02Icon} size={14} className="mr-2 text-muted-foreground" />
              Heatmaps
            </DropdownMenuItem>
            <DropdownMenuItem onClick={go('/funnel-analyzer')} className="cursor-pointer text-xs">
              <HugeiconsIcon icon={FilterIcon} size={14} className="mr-2 text-muted-foreground" />
              Funnels
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onOpenSiteSettings(property, { tab: 'install' })
              }}
              className="cursor-pointer text-xs"
            >
              <HugeiconsIcon icon={SourceCodeIcon} size={14} className="mr-2 text-muted-foreground" />
              Install tracking script
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation()
                onOpenSiteSettings(property, { tab: 'general' })
              }}
              className="cursor-pointer text-xs"
            >
              <HugeiconsIcon icon={Settings02Icon} size={14} className="mr-2 text-muted-foreground" />
              Site settings
            </DropdownMenuItem>
            {onArchive && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onArchive(property)
                }}
                className="cursor-pointer text-xs text-destructive focus:text-destructive"
              >
                <HugeiconsIcon icon={Delete01Icon} size={14} className="mr-2" />
                Archive site
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  )
}

function RowAction({
  icon,
  label,
  onClick,
}: {
  icon: IconSvgElement
  label: string
  onClick: (e: React.MouseEvent) => void
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-foreground/60 transition-colors hover:bg-muted hover:text-foreground [&_svg]:block [&_svg]:shrink-0"
        >
          <HugeiconsIcon icon={icon} size={15} />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

// ── Loading skeleton ────────────────────────────────────────────────────────

/** Bar widths per row from Paper "Home · Loading": site, visitors, delta, events, tests. */
const SKELETON_ROWS: Array<[number, number, number, number, number]> = [
  [96, 40, 34, 36, 56],
  [124, 32, 30, 40, 40],
  [84, 40, 38, 32, 48],
  [110, 36, 32, 40, 40],
  [72, 32, 36, 36, 52],
]

/**
 * The Sites card while the hub loads: the real header and column row, five
 * 52px rows of bars at the loaded geometry (bar height = the text it stands
 * in for). No workspace rows: we don't know the grouping yet.
 */
export function SitesGridSkeleton() {
  const priorMonthLabel = subMonths(new Date(), 1).toLocaleDateString('en-US', { month: 'long' })
  const headCell = cn('text-xs font-medium leading-[14px]', META)
  return (
    <section className="surface-raised overflow-clip" aria-busy="true" aria-label="Sites loading">
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-border/80 px-4">
        <h2 className="text-sm font-semibold leading-[18px] text-foreground">Sites</h2>
        <Skeleton className="h-[13px] w-14" />
        <div className="flex-1" />
        <div className="hidden h-8 w-[220px] shrink-0 items-center gap-2 rounded-lg bg-card px-2.5 shadow-[0_0_0_1px_var(--border)] sm:flex">
          <HugeiconsIcon icon={Search01Icon} size={13} className="shrink-0 text-foreground/40" />
          <span className="text-[13px] leading-4 text-foreground/45">Find a site</span>
        </div>
      </div>
      <table className="w-full table-fixed border-collapse text-left">
        <colgroup>
          <col className="lg:w-[306px]" />
          <col className="hidden w-[110px] md:table-column" />
          <col className="w-[80px]" />
          <col className="hidden w-[90px] md:table-column" />
          <col className="hidden w-[80px] lg:table-column" />
          <col className="hidden w-[92px] lg:table-column" />
          <col className="hidden lg:table-column" />
          <col className="w-[46px]" />
        </colgroup>
        <thead>
          <tr className={cn('h-[34px]', ROW_BORDER)}>
            <th scope="col" className="p-0 pl-4 align-middle"><span className={headCell}>Site</span></th>
            <th scope="col" className="hidden p-0 align-middle md:table-cell"><span className={headCell}>Trend</span></th>
            <th scope="col" className="p-0 text-right align-middle"><span className={headCell}>Visitors MTD</span></th>
            <th scope="col" className="hidden p-0 text-right align-middle md:table-cell"><span className={headCell}>vs {priorMonthLabel}</span></th>
            <th scope="col" className="hidden p-0 text-right align-middle lg:table-cell"><span className={headCell}>Events</span></th>
            <th scope="col" className="hidden p-0 text-right align-middle lg:table-cell"><span className={headCell}>Tests</span></th>
            <th scope="col" colSpan={2} className="hidden p-0 pr-4 text-right align-middle lg:table-cell"><span className={headCell}>Actions</span></th>
            <th scope="col" className="p-0 pr-4 lg:hidden" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {SKELETON_ROWS.map(([site, visitors, delta, events, tests], i) => (
            <tr key={i} className={cn('h-[52px]', ROW_BORDER)}>
              <td className="py-0 pl-4 pr-3 align-middle">
                <div className="flex items-center gap-2.5">
                  <Skeleton className="size-[22px] shrink-0 rounded-[6px]" />
                  <Skeleton className="h-3.5" style={{ width: site }} />
                </div>
              </td>
              <td className="hidden p-0 align-middle md:table-cell">
                <Skeleton className="h-5 w-[90px] bg-muted" />
              </td>
              <td className="p-0 align-middle">
                <Skeleton className="ml-auto h-3.5" style={{ width: visitors }} />
              </td>
              <td className="hidden p-0 align-middle md:table-cell">
                <Skeleton className="ml-auto h-[13px]" style={{ width: delta }} />
              </td>
              <td className="hidden p-0 align-middle lg:table-cell">
                <Skeleton className="ml-auto h-3.5" style={{ width: events }} />
              </td>
              <td className="hidden p-0 align-middle lg:table-cell">
                <Skeleton className="ml-auto h-[13px]" style={{ width: tests }} />
              </td>
              <td className="hidden p-0 lg:table-cell" />
              <td className="p-0" />
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
